// docs/101: kill streaks, counted on this client only. Every foe one of my units (my heroes and
// my companions) fells adds one to the streak; one of mine going down ends it. Nothing here
// reaches the server or another player: the setting is mine, the count is mine, the call is mine.
import type { Snapshot, ResolutionEntry, ResolutionLog, UnitState } from '../sim/types';
import type { SfxKey } from '../audio';

/** What the announcer says for one kill (KS3): its words and its line in the WARLORD pack. */
export interface StreakCall { words: string; sfx: SfxKey }

/** The ladder, by the kill's place in the streak; the last rung holds from there on. */
const LADDER: StreakCall[] = [
  { words: 'Killshot', sfx: 'announce.killshot' },
  { words: 'Double Kill', sfx: 'announce.double_kill' },
  { words: 'Triple Kill', sfx: 'announce.triple_kill' },
  { words: 'Rampage', sfx: 'announce.rampage' },
  { words: 'Dominating', sfx: 'announce.dominating' },
  { words: 'Unstoppable', sfx: 'announce.unstoppable' },
  { words: 'Annihilation', sfx: 'announce.annihilation' },
  { words: 'Eradication', sfx: 'announce.eradication' },
];
const FIRST_BLOOD: StreakCall = { words: 'First Blood', sfx: 'announce.first_blood' };

/** Every line the ladder can say, to fetch ahead of the first kill. */
export const STREAK_SFX: SfxKey[] = [FIRST_BLOOD.sfx, ...LADDER.map((c) => c.sfx)];

/** The call for the `count`th kill in a row; the fight's very first kill of mine is First Blood. */
export function streakCall(count: number, first: boolean): StreakCall {
  if (count === 1 && first) return FIRST_BLOOD;
  return LADDER[Math.min(count, LADDER.length) - 1]!;
}

/** One kill of mine in a log: the log entry it lands on, the unit that made it, its place in the streak. */
export interface StreakKill { seq: number; killer: number; count: number; call: StreakCall }

/** The streak as it stands between logs. */
export interface StreakState {
  count: number;
  /** Foes of mine felled this fight (First Blood is the first). */
  kills: number;
  /** Who last set a foe burning or bleeding (KS2): a tick that fells it is that unit's kill. */
  burners: Map<number, number>;
}

export const freshStreak = (): StreakState => ({ count: 0, kills: 0, burners: new Map() });

/** What the tally needs to know of the units in a log. */
export interface StreakBoard {
  /** One of my heroes or my companions. */
  mine(unit: number): boolean;
  /** A foe: felling it counts. */
  foe(unit: number): boolean;
  /** The unit a hero id (a companion's included) fights as. */
  heroUnit(heroId: number): number | undefined;
}

/** The unit behind an entry's cause: the hero whose order it was, or the unit reacting. */
function causeUnit(e: ResolutionEntry, board: StreakBoard): number | undefined {
  const c = e.cause;
  if (c.kind === 'Intent') return board.heroUnit(c.ref.heroId);
  if (c.kind === 'Reaction' || c.kind === 'Declaration') return c.actor;
  return undefined;
}

/**
 * Walks one log in order (KS2) and moves `state` along it: every foe felled by one of mine is the
 * next kill in the streak, every one of mine dropped (a hero Downed, a companion felled) ends it.
 * A fall with no one behind it (a burn or a bleed ticking) is the kill of whoever set it.
 */
export function tally(state: StreakState, entries: readonly ResolutionEntry[], board: StreakBoard): StreakKill[] {
  const out: StreakKill[] = [];
  for (const e of entries) {
    const f = e.effect;
    if (f.kind === 'StatusSet' && (f.status === 'Burning' || f.status === 'Bleeding')) {
      const by = causeUnit(e, board);
      if (by !== undefined && by !== f.unit) state.burners.set(f.unit, by);
      continue;
    }
    if (f.kind !== 'Dropped') continue;
    if (board.mine(f.unit)) { state.count = 0; continue; }
    if (!board.foe(f.unit)) continue;
    const killer = f.by ?? state.burners.get(f.unit);
    state.burners.delete(f.unit);
    if (killer === undefined || !board.mine(killer)) continue;
    state.count += 1;
    state.kills += 1;
    out.push({ seq: e.seq, killer, count: state.count, call: streakCall(state.count, state.kills === 1) });
  }
  return out;
}

/** The units a log can name, from the board it starts on and the one after (a foe that arrives mid-pulse). */
export function streakBoard(snapshots: ReadonlyArray<Snapshot | undefined>, owners: Record<number, string>, me: string): StreakBoard {
  const units = new Map<number, UnitState>();
  for (const s of snapshots) for (const u of s?.units ?? []) if (!units.has(u.unitId)) units.set(u.unitId, u);
  const byHero = new Map<number, number>();
  for (const u of units.values()) if (u.heroId !== undefined) byHero.set(u.heroId, u.unitId);
  return {
    mine: (id) => { const h = units.get(id)?.heroId; return h !== undefined && owners[h] === me; },
    foe: (id) => units.get(id)?.faction === 'enemy',
    heroUnit: (heroId) => byHero.get(heroId),
  };
}

// ---- this client's streak --------------------------------------------------------------------

let state = freshStreak();
/** Kills by pulse, then by log entry: the scene celebrates each as its clip plays. */
let kills = new Map<number, Map<number, StreakKill>>();

/** A new fight: the streak starts over. */
export function resetStreak(): void { state = freshStreak(); kills = new Map(); }

/** Counts one log as it starts playing (or is caught up past); keeps the kills for the scene. */
export function countLog(log: ResolutionLog, board: StreakBoard): StreakKill[] {
  const found = tally(state, log.entries, board);
  if (found.length) kills.set(log.pulseIndex, new Map(found.map((k) => [k.seq, k])));
  return found;
}

/** The kill of mine that lands on this entry of this pulse's log, if one does. */
export function streakKillAt(pulseIndex: number, seq: number): StreakKill | undefined {
  return kills.get(pulseIndex)?.get(seq);
}
